import { useMemo, type FC } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

import { useMutation } from "@apollo/client"

import styles from "./KnownIssueCreatePage.module.scss"
import KnownIssueForm from "./KnownIssueForm"

import { graphql } from "@/souhl_admin/gql/__generated__/gql"
import type { KnownIssueFormData } from "@/souhl_admin/utils/forms/knownIssue"

const CreateKnownIssue = graphql(/* GraphQL */ `
	mutation CreateKnownIssue($input: CreateKnownIssueInput!) {
		createKnownIssue(input: $input) {
			knownIssue {
				id
			}
		}
	}
`)

const KnownIssueCreatePage: FC = () => {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()

	const initialData = useMemo(() => {
		const prefill = searchParams.get("prefill")
		if (!prefill) return undefined

		try {
			// Decode UTF-8 encoded base64 (matching the encode in UnknownIssueLogsPage)
			const decoded = JSON.parse(
				decodeURIComponent(escape(atob(prefill)))
			) as Partial<KnownIssueFormData>
			return decoded
		} catch {
			console.error("Failed to parse prefill data")
			return undefined
		}
	}, [searchParams])

	const [createKnownIssue, { loading }] = useMutation(CreateKnownIssue, {
		onCompleted: () => {
			navigate("/souhl_admin/known_issues")
		},
		onError: (error) => {
			console.error("Failed to create known issue:", error)
			alert(`作成に失敗しました: ${error.message}`)
		},
	})

	const handleSubmit = async (data: KnownIssueFormData) => {
		await createKnownIssue({
			variables: {
				input: {
					description: data.description,
					issueType: data.issueType,
					logLevel: data.logLevel,
					logMatchMethod: data.logMatchMethod,
					logKeyword: data.logKeyword,
					logModuleName: data.logModuleName,
					logKind: data.logKind || null,
					// 現状、parseInt が通らないやつは無視している
					logSalonIds: data.logSalonIds
						? data.logSalonIds
								.split(",")
								.map((s) => s.trim())
								.filter((s) => s !== "")
								.map((s) => parseInt(s, 10))
								.filter((n) => !isNaN(n))
						: [],
					slackChannelNameToNotify: data.slackChannelNameToNotify,
					mentionTargetIds: data.mentionTargetIds,
					thresholdCountToNotify: data.thresholdCountToNotify,
					cooldownHoursToNotify: data.enableCooldown
						? data.cooldownHoursToNotify
						: null,
					autoResolveDaysAfterLastActivity:
						data.autoResolveDaysAfterLastActivity,
					shouldSendIosCriticalNotification:
						data.shouldSendIosCriticalNotification,
				},
			},
		})
	}

	const handleCancel = () => {
		navigate("/souhl_admin/known_issues")
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h1 className={styles.title}>Known Issue 作成</h1>
			</div>

			<KnownIssueForm
				initialData={initialData}
				onSubmit={handleSubmit}
				submitLabel="作成"
				isSubmitting={loading}
				onCancel={handleCancel}
			/>
		</div>
	)
}

export default KnownIssueCreatePage
